/**
 * 系统更新公告列表页
 */

import type { ReactElement } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ChevronRight, Loader2, Pin } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { useSystemUpdates } from '@/hooks/useSystemUpdates'
import { cn } from '@/lib/utils'
import type { SystemUpdate } from '@/types/systemUpdate'

const PAGE_SIZE = 10 as const

/**
 * 格式化日期时间
 * @param value ISO 时间字符串
 * @returns 格式化后的时间
 */
function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * 提取内容摘要
 * @param update 公告
 * @returns 摘要文本
 */
function getSummary(update: SystemUpdate): string {
  if (update.summary !== undefined && update.summary.trim() !== '') {
    return update.summary.trim()
  }

  const firstLine = update.contentMd
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line !== '' && !line.startsWith('#'))

  if (firstLine === undefined) {
    return '点击查看完整更新内容。'
  }
  return firstLine.length > 120 ? `${firstLine.slice(0, 120)}...` : firstLine
}

/**
 * 生成分页页码数组
 * @param currentPage 当前页码
 * @param totalPages 总页数
 * @returns 页码数组（-1 表示省略号）
 */
function generatePageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, idx) => idx + 1)
  }

  const pages: number[] = [1]
  const showLeftEllipsis = currentPage > 4
  const showRightEllipsis = currentPage < totalPages - 3

  if (showLeftEllipsis) {
    pages.push(-1)
  }

  const start = showLeftEllipsis ? Math.max(2, currentPage - 1) : 2
  const end = showRightEllipsis ? Math.min(totalPages - 1, currentPage + 1) : totalPages - 1

  for (let page = start; page <= end; page += 1) {
    pages.push(page)
  }

  if (showRightEllipsis) {
    pages.push(-1)
  }

  if (totalPages > 1) {
    pages.push(totalPages)
  }

  return pages
}

/**
 * 系统更新公告列表页
 * @returns ReactElement
 */
export function SystemUpdatesPage(): ReactElement {
  const [currentPage, setCurrentPage] = useState<number>(1)

  const {
    data: updateResult,
    isLoading,
    error,
  } = useSystemUpdates({
    current: currentPage,
    pageSize: PAGE_SIZE,
  })

  /**
   * 切换页码
   * @param page 目标页码
   */
  function handlePageChange(page: number): void {
    const totalPages = updateResult?.totalPages ?? 1
    if (page < 1 || page > totalPages) {
      return
    }
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * 渲染分页
   * @returns ReactElement | null
   */
  function renderPagination(): ReactElement | null {
    if (updateResult === undefined || updateResult.totalPages <= 1) {
      return null
    }

    const pages = generatePageNumbers(currentPage, updateResult.totalPages)

    return (
      <Pagination className="mt-8">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={(): void => {
                handlePageChange(currentPage - 1)
              }}
              className={cn(currentPage <= 1 && 'pointer-events-none opacity-50')}
            />
          </PaginationItem>

          {pages.map((page, idx) => {
            if (page === -1) {
              return (
                <PaginationItem key={`ellipsis-${String(idx)}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              )
            }
            return (
              <PaginationItem key={page}>
                <PaginationLink
                  isActive={page === currentPage}
                  onClick={(): void => {
                    handlePageChange(page)
                  }}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            )
          })}

          <PaginationItem>
            <PaginationNext
              onClick={(): void => {
                handlePageChange(currentPage + 1)
              }}
              className={cn(
                currentPage >= updateResult.totalPages && 'pointer-events-none opacity-50'
              )}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">加载系统更新中...</span>
      </div>
    )
  }

  if (error !== null) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center">
        <p className="text-sm text-destructive">加载失败：{error.message}</p>
      </div>
    )
  }

  if (updateResult === undefined || updateResult.list.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-10 text-center">
        <h2 className="text-lg font-semibold">暂无系统更新</h2>
        <p className="mt-2 text-sm text-muted-foreground">后续版本更新会展示在这里。</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">系统更新</h1>
        <p className="text-sm text-muted-foreground">
          查看平台最近的功能更新、优化与修复记录。
        </p>
      </div>

      <div className="grid gap-4">
        {updateResult.list.map((update) => (
          <Link key={update.id} to={`/updates/${update.id}`} className="block">
            <Card className="transition-colors hover:border-primary/50">
              <CardHeader className="gap-2">
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="text-lg leading-7">{update.title}</CardTitle>
                  {update.isPinned ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-700">
                      <Pin className="h-3.5 w-3.5" />
                      置顶
                    </span>
                  ) : null}
                </div>
                <CardDescription className="flex items-center gap-1.5 text-xs">
                  <Calendar className="h-3.5 w-3.5" />
                  发布时间：{formatDateTime(update.publishedAt)}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex items-center justify-between gap-3 pt-0">
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {getSummary(update)}
                </p>
                <span className="inline-flex items-center gap-1 text-sm text-primary">
                  查看详情
                  <ChevronRight className="h-4 w-4" />
                </span>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {renderPagination()}
    </div>
  )
}
